/** U1/U2 — جدول مقارنة موحّد: المؤشر (خلية مدموجة على 3 صفوف) · الحالي/الأسبوع الماضي/الشهر الماضي · عمود لكل فرع. */
import React from "react";
import { InfoTip } from "./components/InfoTip";
import { zVar } from "./lib/zIndex";
import { TABLE, TH, TD, SalesColGroup, salesTableMinWidth } from "./lib/salesTable";

const BROWN = "#3D2A1C";
const n = (v, d = 2) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: d });
export const FMT = {
  money: (v) => `${n(v)} ﷼`, count: (v) => n(v, 0), pct: (v) => `${n(v, 1)}%`,
  ratio: (v) => Number(v || 0).toFixed(2),
};
export const PERIODS = [["cur", "الحالي"], ["week", "الأسبوع الماضي"], ["month", "الشهر الماضي"]];

// E4 (2026-02) — نقطة الحقيقة الواحدة لقرار «أي صفوف تُرسم لهذا العرض» —
// تُقرأ من SalesCompareTable و BranchCustomerCards معاً، لا منطق منفصل في
// كل مكوّن. القرار VIEW-based بحت: هل هذا النوع من الفترات (أسبوع/شهر)
// ينطبق أصلاً على هذا العرض (ids[p] غير null)؟ هذا فقط. توفّر البيانات
// الفعلية (has_data/available) لا يُخفي الصف إطلاقاً — خلايا «لا بيانات»
// تُرسم مكانها (Cell أدناه عبر unavailable).
export function periodsForView(ids) {
  return PERIODS.filter(([p]) => p === "cur" || !!ids?.[p]);
}

export const GREEN = "#0F766E";
export const RED = "#B91C1C";
export const MUTE_COLOR = "#78716C";
const MUTE = MUTE_COLOR;  // اسم محلي قديم — نفس القيمة، تجنّب تكرار التعديل في كل استخدام أدناه

/** G2 (2026-02) — لون الفرق حسب polarity وحدها، لإعادة الاستخدام خارج
 * جدول المقارنة (بطاقات KPI مفردة في صفحات أخرى) دون تكرار منطق الإشارة. */
export function polarityColor(diff, polarity = "higher_is_better") {
  if (polarity === "neutral" || diff === 0) return MUTE_COLOR;
  const isUp = diff > 0;
  if (polarity === "lower_is_better") return isUp ? RED : GREEN;
  return isUp ? GREEN : RED;
}

/** التغيّر: أعداد/مبالغ ⇒ نسبة · نِسب ⇒ نقاط · فواتير/عميل ⇒ فرق مطلق.
 * AG11 (2026-10-28) — عطل: اللون كان دوماً «زيادة=أخضر» بصرف النظر عن
 * معنى المقياس (مثال: زيادة «المفقودون» رُسمت خضراء رغم أنها تراجع).
 * الإصلاح: polarity (من core/report_metrics_registry.py::FIELD_POLARITY
 * عبر الصفوف المستهلِكة) يقرّر اللون وحده الآن؛ الاتجاه (▲/▼) يبقى
 * يعكس الواقع الفعلي للفرق دوماً، بصرف النظر عن اللون.
 * جولة O2 (البند 2) — قاعدة واحدة في المحرك لكل الكتل + صفحة المبيعات:
 *  1) unavailable=true (الطرف السابق «لم يُفتتح»/«غير متاح» من الخادم) ⇒
 *     "—" فقط، لا سهم ولا نسبة مضلِّلة.
 *  2) مرجع صفر حقيقي (p===0, c!==0) ⇒ "من 0" بلا نسبة — نُسِخ G3 (2026-02)
 *     القديم (كان يكتب "جديد" هنا؛ قرار أحدث صريح: الكلمة ممنوعة لأن c قد
 *     يكون عميلاً عائداً لا عميلاً جديداً فعلاً، فقط مرجعه صفر).
 *  3) dailyAvg={curDays,prevDays} (الوحدتان المقارَنتان بطول فعلي مختلف —
 *     جارية/جزئية مقابل كاملة، أو نافذة سابقة تتداخل جزئياً مع ما قبل أول
 *     بيانات فعلية) ⇒ النسبة على المعدّل اليومي (قيمة/أيامها الفعلية) لا
 *     الرقم الخام، وتوسَم صراحةً "(بالمتوسط اليومي)" — عطل إنتاج موثَّق:
 *     أسبوع 4-10 أكتوبر (7 أيام) قورن بأسبوع 1-3 أكتوبر (3 أيام فعليين
 *     فقط ضمن نافذة نظرياً 7 أيام) فظهرت ▲99.6%/▲514.6%/▲125.1%/▲74.9%
 *     زائفة — نسبة خام على مقامين بطول مختلف فعلياً، لا نمو حقيقي. */
export function Delta({ kind, cur, prev, testId, polarity = "higher_is_better", unavailable = false, dailyAvg = null }) {
  if (unavailable) {
    return <span data-testid={testId} style={{ color: MUTE }}> —</span>;
  }
  const c = Number(cur || 0), p = Number(prev || 0), d = c - p;
  const colorFor = (isUp) => polarityColor(isUp ? 1 : -1, polarity);
  if (kind !== "pct" && kind !== "ratio" && p === 0) {
    if (c === 0) return <span data-testid={testId} style={{ color: MUTE }}> = 0</span>;
    return <span data-testid={testId} style={{ color: colorFor(true), fontWeight: 700 }}> من 0</span>;
  }
  if (kind !== "pct" && kind !== "ratio" && dailyAvg?.curDays > 0 && dailyAvg?.prevDays > 0
      && dailyAvg.curDays !== dailyAvg.prevDays) {
    const rc = c / dailyAvg.curDays, rp = p / dailyAvg.prevDays, rd = rc - rp;
    if (rp === 0) return <span data-testid={testId} style={{ color: MUTE }}> من 0 (بالمتوسط اليومي)</span>;
    if (rd === 0) return <span data-testid={testId} style={{ color: MUTE }}> = 0% (بالمتوسط اليومي)</span>;
    return <span data-testid={testId} style={{ color: colorFor(rd > 0), fontWeight: 700 }}>
      {" "}{rd > 0 ? "▲" : "▼"} {n(Math.abs((rd / rp) * 100), 1)}% (بالمتوسط اليومي)</span>;
  }
  if (d === 0) return <span data-testid={testId} style={{ color: MUTE }}> =</span>;
  const up = d > 0;
  let txt;
  if (kind === "pct") txt = `${n(Math.abs(d), 1)} نقطة`;
  else if (kind === "ratio") txt = Math.abs(d).toFixed(2);
  else txt = `${n(Math.abs((d / p) * 100), 1)}%`;
  return <span data-testid={testId} style={{ color: colorFor(up), fontWeight: 700 }}> {up ? "▲" : "▼"} {txt}</span>;
}

function Cell({ v, kind, period, testId, unavailable, isError, polarity }) {
  const first = period === "cur";
  // E4 — فترة غير متاحة لهذا الطرف (has_data=false أو available=false من
  // الخادم) ⇒ «لا بيانات» بدل شرطة صامتة أو إخفاء الصف كله؛ السبب في ⓘ
  // تسمية الفترة (الصف نفسه يبقى مرسومًا دائمًا).
  // T3 (2026-02) — عطل إنتاج: فشل تحميل حقيقي (مهلة/شبكة) كان يَصِل إلى
  // هنا بنفس مسار «لا بيانات» تماماً فيبدو كتصنيف RFM فعلي لا عطل. الآن
  // تُميَّز الخليتان: حمراء صريحة «تعذّر التحميل» لفشل الطلب، رمادية
  // محايدة «لا بيانات» لغياب فواتير فعلي في النافذة.
  if (isError && !first) {
    return <td data-testid={testId} style={{ ...TD(false), padding: "2px 8px", fontSize: 11, color: RED, fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>تعذّر التحميل</td>;
  }
  if (unavailable && !first) {
    return <td data-testid={testId} style={{ ...TD(false), padding: "2px 8px", fontSize: 11, color: MUTE, whiteSpace: "nowrap" }}>لا بيانات</td>;
  }
  if (!v) return <td data-testid={testId} style={TD(first)}>—</td>;
  const fmt = FMT[kind === "pct" ? "pct" : kind === "ratio" ? "ratio" : v.fmt || kind];
  if (period === "cur") {
    const body = <b style={{ fontSize: 15, color: BROWN, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{fmt(v.cur)}</b>;
    return (
      <td data-testid={testId} style={{ ...TD(true), padding: "6px 8px" }}>
        {/* P2 (2026-02) — عطل إنتاج: 🏆 كانت تلي الرقم مباشرة داخل خلية
            whiteSpace:nowrap؛ إن ضاقت الخلية (عمود بيانات نحيف) يفيض
            النص خارج حدودها بصريًا فيتراكب مع الرقم/الخلية المجاورة.
            الإصلاح: الرقم والأيقونة عنصرا flex يلتفّان معًا كوحدة واحدة
            (flexWrap:wrap) بدل فيضان نص غير مقطوع — لا تراكب ممكن بنيويًا. */}
        <span style={{ display: "inline-flex", alignItems: "baseline", flexWrap: "wrap", gap: 4, maxWidth: "100%" }}>
          {v.onOpen ? (
            <button type="button" data-testid={`${testId}-open`} onClick={v.onOpen}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline dotted" }}>{body}</button>
          ) : <span data-testid={`${testId}-value`}>{body}</span>}
          {v.trophy && <span data-testid={`${testId}-trophy`} aria-label="رقم قياسي" style={{ fontSize: 12, whiteSpace: "nowrap" }}>🏆</span>}
          {/* G1 (2026-02) — ⚠ فترة بيانات غير موثوقة تشمل هذا اليوم/النافذة
              (من /pos/records/exclusions نفسها عبر core.records_exclusions
              المشتركة) — نص السبب في title (لمس/تمرير)، ويُطبع كنص صريح
              تحت الصف (G6، لأن title لا يظهر على الورق). */}
          {v.unreliable && (
            <span data-testid={`${testId}-unreliable`} style={{ fontSize: 12, whiteSpace: "nowrap" }}>
              <InfoTip text={`بيانات غير موثوقة: ${v.unreliable.reason} (${v.unreliable.from} → ${v.unreliable.to})`} testId={`${testId}-unreliable-info`} />⚠
            </span>
          )}
        </span>
        {v.unreliable && (
          <div className="report-print-only" data-testid={`${testId}-unreliable-print`}
            style={{ fontSize: 10, color: "#B45309" }}>
            ⚠ بيانات غير موثوقة: {v.unreliable.reason} ({v.unreliable.from} → {v.unreliable.to})
          </div>
        )}
        {/* Q2 (2026-02) — «منها تكر X»: تفصيل فرعي لا طرح، يظهر فقط عندما
            دُمجت فواتير تكر فعلياً ضمن هذا الرقم (X>0). */}
        {v.takerNote && (
          <div data-testid={`${testId}-taker-note`} style={{ fontSize: 10, color: MUTE, marginTop: 2, whiteSpace: "nowrap" }}>
            {v.takerNote}
          </div>
        )}
      </td>
    );
  }
  const prev = v[period];
  // جولة O2 (البند 2) — "—" صريحة عند غياب حقيقي (لا مجرد خلية فارغة)،
  // وتمرير dailyAvg (طول الوحدتين الفعلي) لـDelta كي يقرّر نمط النسبة.
  const curDays = v.cur_days;
  const prevDays = v[`${period}_days`];
  const dailyAvg = (curDays != null && prevDays != null) ? { curDays, prevDays } : null;
  return (
    <td data-testid={testId} style={{ ...TD(false), padding: "2px 8px", fontSize: 11, color: MUTE, whiteSpace: "nowrap" }}>
      {prev == null ? <span data-testid={`${testId}-na`}>—</span> : <>
        {/* جولة O2 (البند 10) — عطل إنتاج: الرقم والسهم/النسبة كانا على
            سطر واحد متجاورَين (inline)؛ مع whiteSpace:nowrap وعمود ضيق على
            A4 يفيض المجموع فعلياً فوق عمود الفرع المجاور. الإصلاح: كل
            محتوى صندوقه الخاص — الرقم سطر، والتغيّر سطر مستقل تحته. */}
        <div style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(prev)}</div>
        <div><Delta kind={kind} cur={v.cur} prev={prev} testId={`${testId}-delta`} polarity={polarity}
                    unavailable={!!v[`${period}_na`]} dailyAvg={dailyAvg} /></div>
      </>}
      {(v.unreliable_window?.[period] || v[`${period}_unreliable`]) ? (() => {
        const ex = v.unreliable_window?.[period] || v[`${period}_unreliable`];
        return <>
          <span data-testid={`${testId}-unreliable-window`} style={{ marginInlineStart: 4 }}>
            <InfoTip text={`تشمل هذه النافذة يوماً غير موثوق: ${ex.reason}`} testId={`${testId}-unreliable-window-info`} />⚠
          </span>
          <div className="report-print-only" data-testid={`${testId}-unreliable-window-print`}
            style={{ fontSize: 9, color: "#B45309", whiteSpace: "normal" }}>
            ⚠ تشمل هذه النافذة فترة غير موثوقة: {ex.reason}
          </div>
        </>;
      })() : null}
    </td>
  );
}
export default function CompareTable({ testId, note, cols, rows, periods = PERIODS }) {
  // G5 (2026-02) — عطل إنتاج: عمود التسمية كان width:170 ثابت مع
  // whiteSpace:nowrap؛ أي تسمية أطول من ~170بكسل (شائع بأسماء المقاييس
  // العربية) تفيض فعلياً خارج حدود الخلية فتتراكب مع عمود البيانات
  // المجاور. الإصلاح: التفاف طبيعي على سطرين (lineClamp 2) بدل nowrap —
  // لا تراكب ممكن بنيويًا الآن، وبقي العرض الثابت نفسه (لا رقم جديد).
  // H1 (2026-02) — عطل إنتاج: أُزيل position:"sticky" الذي كان هنا — جعل
  // خلية تحمل rowSpan={periods.length} (تمتد صفوف المقارنة) و/أو colSpan
  // (رأس الجدول) "sticky" داخل جدول قابل للتمرير أفقياً (overflowX:auto)
  // يجعل محرك التخطيط في بعض المتصفحات (الإنتاج تحديداً) يُعيد حساب عرض
  // عمود الخلية اللاصقة بمعزل عن بقية الأعمدة، فينزاح كل عمود بيانات تالٍ
  // خانة واحدة يساراً نسبةً لرأسه — هذا هو سبب H1 (عمود البدراني فارغ،
  // قيمه تحت العزيزية، العزيزية تحت الإجمالي، الإجمالي تحت الحالي).
  const titleCellStyle = { ...TD(true), padding: "6px 8px", fontWeight: 800,
    background: "#FFFDF8" };
  // L2 (2026-02) — عطل إنتاج: display:"-webkit-box" كان على الـ<td> نفسه
  // (titleCellStyle)، فيفقد الـ<td> دوره كخلية جدول (display:table-cell)
  // ويتجاهل المتصفح colSpan={2} عليه — فينزاح كل عمود بيانات تالٍ خانة
  // واحدة (نفس نمط عطل H1 أعلاه، لكن عبر display لا sticky). الإصلاح:
  // القصّ على سطرين (line-clamp) على <div> داخلي فقط، والـ<td> يبقى خلية
  // جدول عادية بلا أي display مخصّص.
  const titleClampStyle = { whiteSpace: "normal", wordBreak: "break-word", overflow: "hidden",
    display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" };
  return (
    <div style={{ overflowX: "auto" }}>
      <table data-testid={testId} data-table-style="sales" style={{ ...TABLE, minWidth: salesTableMinWidth(cols.length, true), fontSize: 12, color: BROWN }}>
        <SalesColGroup dataCols={cols.length} withPeriodCol />
        <thead>
          <tr style={{ background: "rgba(170,149,102,0.14)" }}>
            <th colSpan={2} style={{ ...TH, textAlign: "right", fontSize: 10, color: MUTE, fontWeight: 600 }}
              data-testid={`${testId}-basis`}>{note}</th>
            {cols.map((c) => <th key={c.id} data-testid={`${testId}-col-${c.id}`} style={{ ...TH, textAlign: "right", verticalAlign: "top", whiteSpace: "nowrap" }}>{c.name}{c.sub}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => r.cells ? (
            // B2 — صف بخانة لكل عمود من cols نفسها (لا شبكة خاصة).
            <tr key={r.code} data-testid={`${testId}-${r.code}-row`}>
              <td colSpan={2} style={titleCellStyle}><div style={titleClampStyle}>{r.label}</div></td>
              {cols.map((c) => (
                <td key={c.id} data-testid={`${testId}-${r.code}-${c.id}`} data-col={c.id}
                  style={{ ...TD(true), verticalAlign: "top" }}>{r.cells(c)}</td>
              ))}
            </tr>
          ) : (() => {
            // G1 (2026-02) — ⓘ واحدة فقط لكل مقياس بجانب اسمه: نافذة المقارنة
            // (من D3) لكل فترة تُدمَج هنا في نص واحد بدل أيقونة منفصلة بكل
            // صف مقارنة (كانت تُعدَّد ⓘ في جدول الفروع بعدد الصفوف لا المقاييس).
            const windows = periods
              .map(([, label, info, unavailable]) => (info || unavailable) ? `${label}: ${info || unavailable}` : null)
              .filter(Boolean);
            const combinedInfo = [r.info, ...windows].filter(Boolean).join(" · ");
            return periods.map(([p, label, info, unavailable, isError], i) => (
              <tr key={`${r.code}-${p}`} data-testid={`${testId}-${r.code}-${p}`}>
                {/* H1 (2026-02) — عطل إنتاج (انزياح عمودي في صفوف المقارنة):
                    كانت خلية العنوان rowSpan={periods.length} على i===0
                    فقط، فتصبح صفوف المقارنة (i>0) بخلية واحدة أقل من صف
                    «الحالي» ومن صف الرأس تحت table-layout:fixed — يكفي أن
                    يختلف محرك عرض واحد (إنتاج) في كيفية حساب عرض عمود
                    خليةٍ تمتد صفوفاً (rowSpan) داخل جدول fixed-layout حتى
                    ينزاح كل عمود بيانات تالٍ خانة. الإصلاح: كل صف — بلا
                    استثناء — يبدأ بخلية عمود التسمية نفسها (بلا rowSpan
                    إطلاقاً)؛ نصّها يظهر فقط في صف «الحالي» (visibility
                    hidden لما بعده، لا display:none، فيبقى العرض محجوزاً
                    لنفس العمود تماماً بلا أي فرق بصري عن ذي قبل). */}
                <td data-testid={i === 0 ? r.cardTestId : undefined}
                  style={{ ...titleCellStyle, verticalAlign: "top",
                           visibility: i === 0 ? "visible" : "hidden",
                           borderTop: i === 0 ? titleCellStyle.borderTop : "none",
                           padding: i === 0 ? titleCellStyle.padding : 0 }}>
                  <div style={titleClampStyle}>{r.label}</div>{combinedInfo && <> <InfoTip testId={`${testId}-${r.code}-infotip`} text={combinedInfo} /></>}
                </td>
                {/* هرمية الصفوف: «الحالي» (i===0) أبرز خطاً وارتفاعاً؛ صفوف
                    المقارنة مضغوطة (خط أصغر ورمادي وحشو رأسي أقل) دوماً. */}
                <td style={{ ...TD(i === 0), padding: i ? "2px 8px" : "6px 8px",
                             fontSize: i ? 11 : 13, color: i ? MUTE : BROWN, fontWeight: i ? 500 : 800,
                             whiteSpace: "nowrap" }}>
                  {label}
                </td>
                {cols.map((c) => <Cell key={c.id} v={r.values[c.id]} kind={r.kind} period={p} unavailable={unavailable} isError={isError}
                  polarity={r.polarity} testId={`${testId}-${r.code}-${c.id}-${p}`} />)}
              </tr>
            ));
          })())}
        </tbody>
      </table>
    </div>
  );
}

/** U4 — نافذة صغيرة: الرقم القياسي للمؤشر والفرع للمستوى المعروض + تاريخه + الفرق + «مستبعد». */
export function RecordPopover({ title, item, fmt, levelLabel, onClose }) {
  const b = item?.best;
  return (
    <div data-testid="record-popover" role="dialog" onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: zVar("modal"), display: "grid", placeItems: "center" }}>
      <div onClick={(e) => e.stopPropagation()} dir="rtl"
        style={{ background: "#FFFDF8", borderRadius: 14, padding: "14px 18px", minWidth: 260, color: BROWN, fontSize: 13, display: "grid", gap: 6 }}>
        <b data-testid="record-popover-title">{title}</b>
        {!b ? <span data-testid="record-popover-none" style={{ color: MUTE }}>لا رقم قياسي لهذا المؤشر بعد</span> : (
          <>
            <span data-testid="record-popover-best">الرقم القياسي ({levelLabel}): <b>{fmt(b.value)}</b> · {b.from === b.to ? b.from : `${b.from} → ${b.to}`}</span>
            <span data-testid="record-popover-gap">{item.is_record ? "🏆 الرقم الحالي رقم قياسي" : `الفرق عنه: ${fmt(item.gap)}`}</span>
            {item.excluded && <span data-testid="record-popover-excluded" style={{ color: "#B45309" }}>مستبعد: {item.excluded.reason} ({item.excluded.from} → {item.excluded.to})</span>}
          </>
        )}
        <button type="button" data-testid="record-popover-close" onClick={onClose}
          style={{ justifySelf: "start", border: "1px solid var(--border)", background: "white", borderRadius: 8, padding: "2px 10px", cursor: "pointer" }}>إغلاق</button>
      </div>
    </div>
  );
}

/** U4 — سطر الرقم القياسي أعلى قائمة العملاء. */
export function RecordLine({ item, fmt, levelLabel }) {
  if (!item?.best) return null;
  return (
    <div data-testid="clm-record-line" style={{ fontSize: 12, color: "#B45309", marginTop: 3 }}>
      🏆 الرقم القياسي ({levelLabel}): {fmt(item.best.value)} · {item.best.from === item.best.to ? item.best.from : `${item.best.from} → ${item.best.to}`}
      {item.is_record ? " · الحالي رقم قياسي" : ` · الفرق ${fmt(item.gap)}`}
    </div>
  );
}
